"use client";

import React, {useState, useRef, useEffect} from "react";
import {
    Box,
    Typography,
    Collapse,
    IconButton,
    Link,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Tooltip,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import type {SxProps, Theme} from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {playTextAsSpeech, getCurrentPlayingText, stopCurrentAudio} from "../lib/textToSpeech";
import {stripMarkdown} from "../lib/markdownSpeech";
import {normalizeMarkdownTables} from "../lib/normalizeMarkdownTables";
import {PRICE_RATING_COLOURS, type PriceRating} from "../lib/priceRatingColours";

export type {PriceRating};

// AI ANALYSIS DATA
export interface AiAnalysisData {
    /**
     * Subset of prospect_listings columns produced by the Gemini AI analysis,
     * plus the generated suggested range and price rating. Mirrors the shape
     * returned by the runAiAnalysis server action and is the input contract
     * for AiAnalysisSection.
     */

    aiResellOverview: string | null;
    aiMakeAndModelHistory: string | null;
    aiWorkAndRepairs: string | null;
    aiResellNotes: string | null;
    aiCampervanConversion: string | null;
    aiValueAddImprovements: string | null;
    aiTargetMarket: string | null;
    aiBuyPriceLow: number | null;
    aiBuyPriceHigh: number | null;
    aiRepairCost: number | null;
    aiSellPriceLow: number | null;
    aiSellPriceHigh: number | null;
    aiSuggestedPriceLow: number | null;
    aiSuggestedPriceHigh: number | null;
    priceRating: PriceRating | null;
}

// AI ANALYSIS SECTION PROPS
interface AiAnalysisSectionProps {
    data: AiAnalysisData;
    askingPrice: number | null;
    currencySymbol: string | null;
    canViewPricing: boolean;
    defaultExpanded?: boolean;
    showSpeakButtons?: boolean;
    splitLayout?: boolean;
    targetMarketAfterPricing?: boolean;
    guestPreview?: boolean;
    cardHovered?: boolean;
    onExpanderHoverChange?: (hovering: boolean, event: React.MouseEvent) => void;
    headerSx?: SxProps<Theme>;
    bodySx?: SxProps<Theme>;
}
/** Props accepted by the AI Assistant pane. Pricing context is passed in so
 *  the markup can be reused for prospect cards and the manual-entry editor.
 *  splitLayout lets a parent grid place the expander header and the expanded
 *  body independently, which is how Classics AI pins the toggle to the right
 *  while keeping the narrative content left-aligned. guestPreview limits the
 *  pane to Overview and Make and Model History and adds a login link.
 *  cardHovered opens the pane while the pointer is over the parent card,
 *  unless the visitor has opened it with the expander. onExpanderHoverChange
 *  reports when the pointer is over that expander so the card can leave it
 *  out of the hover area. */

// SPEAK BUTTON PROPS
interface SpeakButtonProps {
    /* Props for the optional per-section read-aloud control. */

    text: string;
    title: string;
    playingText: string | null;
    onSpeak: (text: string) => void;
    visible: boolean;
}

// SPEAK BUTTON
function SpeakButton({text, title, playingText, onSpeak, visible}: SpeakButtonProps) {
    /**
     * Icon button that starts or stops speech for a section. Hidden when
     * the parent has disabled read-aloud.
     */

    if (!visible) {
        return null;
    }

    return (
        <IconButton
            onClick={() => onSpeak(text)}
            size="small"
            sx={{p: 0.5, minWidth: "auto"}}
            title={title}
            color={playingText === text ? "secondary" : "inherit"}
        >
            <VolumeUpIcon fontSize="small" />
        </IconButton>
    );
}

// PRICE RATING TEXT COLOURS
const PRICE_RATING_TEXT_COLOURS: Record<PriceRating, string> = {
    "Great Price": "#0E3D20",
    "Good Price": "#fff",
    "Fair Price": "#fff",
    "Fully Priced": "#fff",
};
/** Label colours. The bright Great Price green uses dark text so it stays readable. */

// PRICE RATING HINTS
const PRICE_RATING_HINTS: Record<PriceRating, string> = {
    "Great Price": "Well below the AI value range",
    "Good Price": "At or just below the low end",
    "Fair Price": "Within the range",
    "Fully Priced": "At or above the high end",
};
/** Short explanation shown when a rating pill is hovered. */

// SUGGESTED PRICE HINT
const SUGGESTED_PRICE_HINT = "... accounts for the estimated repair costs";
/** Hover text for the suggested-price label. The range already includes repair costs. */

// PRICE RATING TAG PROPS
interface PriceRatingTagProps {
    /* Props for the coloured price-rating pill. */

    rating: PriceRating;
}

// PRICE RATING TAG
function PriceRatingTag({rating}: PriceRatingTagProps) {
    /**
     * Renders one stored price rating as a rounded tag in the graphic's colours.
     */

    return (
        <Tooltip title={PRICE_RATING_HINTS[rating]}>
            <Box
                component="span"
                sx={{
                    display: "inline-block",
                    bgcolor: PRICE_RATING_COLOURS[rating],
                    color: PRICE_RATING_TEXT_COLOURS[rating],
                    borderRadius: "999px",
                    px: 1.5,
                    py: 0.35,
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                    lineHeight: 1.4,
                    whiteSpace: "nowrap",
                }}
            >
                {rating}
            </Box>
        </Tooltip>
    );
}

// GUEST LOGIN PROMPT
export function GuestLoginPrompt() {
    /**
     * Asks anonymous visitors to log in or sign up for the full AI Assistant.
     * Keeps the current path in returnTo.
     */

    const theme = useTheme();
    const linkSx = {
        color: theme.palette.error.main,
        textDecorationColor: theme.palette.error.main,
    };

    // HANDLE AUTH CLICK
    const handleAuthClick = (event: React.MouseEvent<HTMLAnchorElement>, path: string) => {
        /**
         * Sends the visitor to login or signup and preserves the current
         * page as returnTo when the path is a safe relative URL.
         */

        const returnTo = `${window.location.pathname}${window.location.search}`;
        if (!returnTo.startsWith("/") || returnTo.startsWith("//")) {
            return;
        }

        event.preventDefault();
        window.location.assign(`${path}?returnTo=${encodeURIComponent(returnTo)}`);
    };

    return (
        <Typography variant="body2">
            <Link
                href="/auth/login"
                onClick={(event) => handleAuthClick(event, "/auth/login")}
                sx={linkSx}
            >
                Log In
            </Link>
            {" or "}
            <Link
                href="/auth/signup"
                onClick={(event) => handleAuthClick(event, "/auth/signup")}
                sx={linkSx}
            >
                Sign Up
            </Link>
            {" for the full AI Assistant"}
        </Typography>
    );
}

// AI ANALYSIS SECTION
export default function AiAnalysisSection({
    data,
    askingPrice,
    currencySymbol,
    canViewPricing,
    defaultExpanded = false,
    showSpeakButtons = true,
    splitLayout = false,
    targetMarketAfterPricing = false,
    guestPreview = false,
    cardHovered = false,
    onExpanderHoverChange,
    headerSx,
    bodySx,
}: AiAnalysisSectionProps) {
    /**
     * Expandable AI Assistant pane that renders all AI-generated narrative
     * fields and a pricing table of asking price, suggested range, repair
     * cost, and the stored price rating. Each text section has a speak
     * button that plays the content through the TTS dispatcher unless
     * showSpeakButtons is false. Designed to be embedded inside both the
     * prospect card and the manual-entry editor so the same UI shows
     * everywhere AI analysis is consumed. When guestPreview is set, only
     * Overview and Make and Model History are shown, then a Notes heading
     * with a login and signup prompt. Hovering the parent card opens a
     * collapsed pane until the pointer leaves. Opening it with the expander
     * keeps it open until the expander hides it again.
     */

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [openedManually, setOpenedManually] = useState(defaultExpanded);
    const [hoverDismissed, setHoverDismissed] = useState(false);
    const expanded = openedManually || (cardHovered && !hoverDismissed);
    const [playingText, setPlayingText] = useState<string | null>(null);
    const playingTextRef = useRef<string | null>(null);

    useEffect(() => {
        playingTextRef.current = playingText;
    }, [playingText]);

    useEffect(() => {
        // allow the next hover to open the pane once the pointer has left
        if (!cardHovered) {
            setHoverDismissed(false);
        }
    }, [cardHovered]);

    // HANDLE TOGGLE
    const handleToggle = (): void => {
        /**
         * Opens or hides the pane from the expander. Opening it keeps the
         * pane visible after the pointer leaves. Hiding it while the pointer
         * is still over the card waits until the pointer leaves before hover
         * can open it again.
         */

        if (expanded) {
            setOpenedManually(false);
            if (cardHovered) {
                setHoverDismissed(true);
            }
            return;
        }

        setOpenedManually(true);
        setHoverDismissed(false);
    };

    // HANDLE SPEAK
    const handleSpeak = (text: string): void => {
        /**
         * Toggles speech playback for the supplied text. If the same text is
         * already playing, stops audio; otherwise starts a new playback and
         * highlights the matching speaker icon.
         */

        if (getCurrentPlayingText() === text) {
            stopCurrentAudio();
            setPlayingText(null);
            return;
        }
        setPlayingText(null);
        setPlayingText(text);
        void playTextAsSpeech(text, {onEnded: () => setPlayingText(null)}).then((result) => {
            if ((!result?.success || result?.stopped) && playingTextRef.current === text) {
                setPlayingText(null);
            }
        });
    };

    const overviewText = stripMarkdown(data.aiResellOverview || "");
    const makeAndModelHistoryText = stripMarkdown(data.aiMakeAndModelHistory || "");
    const workAndRepairsMarkdown = normalizeMarkdownTables(data.aiWorkAndRepairs || "");
    const workAndRepairsText = stripMarkdown(data.aiWorkAndRepairs || "");
    const notesText = stripMarkdown(data.aiResellNotes || "");
    const campervanText = stripMarkdown(data.aiCampervanConversion || "");
    const valueAddImprovementsText = stripMarkdown(data.aiValueAddImprovements || "");
    const targetMarketText = stripMarkdown(data.aiTargetMarket || "");

    const targetMarketSection = data.aiTargetMarket ? (
        <Box sx={{mb: 2}}>
            <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1}}>
                <Typography variant="subtitle2" sx={{color: theme.custom.labelColour}}>
                    Target Market
                </Typography>
                <SpeakButton
                    text={targetMarketText}
                    title="Speak target market"
                    playingText={playingText}
                    onSpeak={handleSpeak}
                    visible={showSpeakButtons}
                />
            </Box>
            <Box sx={{
                fontSize: "0.875rem",
                wordBreak: "break-word",
                overflowWrap: "break-word",
                "& p": {margin: "0.5em 0"},
                "& ul, & ol": {margin: "0.5em 0", paddingLeft: "1.5em"},
                "& li": {margin: "0.75em 0"},
                "& h1, & h2, & h3, & h4, & h5, & h6": {margin: "0.75em 0 0.5em 0"},
                "& strong": {fontWeight: "bold"},
                "& em": {fontStyle: "italic"},
            }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.aiTargetMarket}</ReactMarkdown>
            </Box>
        </Box>
    ) : null;

    return (
        <Box sx={splitLayout ? {display: "contents"} : undefined}>
            <Box
                data-ai-assistant-expander=""
                onMouseEnter={onExpanderHoverChange
                    ? (event) => onExpanderHoverChange(true, event)
                    : undefined}
                onMouseLeave={onExpanderHoverChange
                    ? (event) => onExpanderHoverChange(false, event)
                    : undefined}
                sx={[
                    {display: "flex", alignItems: "center", mt: 0, ml: 0},
                    ...(headerSx ? [headerSx] : []),
                ] as SxProps<Theme>}
            >
                <IconButton
                    onClick={handleToggle}
                    sx={{p: 0, ml: 0, mr: 0, minWidth: "auto", color: "inherit"}}
                    aria-expanded={expanded}
                    aria-label={expanded ? "Hide AI Assistant" : "Expand AI Assistant"}
                >
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
                <Typography variant="body2" sx={{ml: 0.5, color: theme.custom.labelColour}}>
                    AI Assistant
                </Typography>
            </Box>
            <Box sx={bodySx}>
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Box data-assistant-hover="" sx={{mt: 1, maxWidth: "100%", overflow: "hidden"}}>
                    {data.aiResellOverview && (
                        <Box sx={{mb: 2}}>
                            <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1}}>
                                <Typography variant="subtitle2" sx={{color: theme.custom.labelColour}}>
                                    Overview
                                </Typography>
                                <SpeakButton
                                    text={overviewText}
                                    title="Speak overview"
                                    playingText={playingText}
                                    onSpeak={handleSpeak}
                                    visible={showSpeakButtons}
                                />
                            </Box>
                            <Box sx={{
                                fontSize: "0.875rem",
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                                "& p": {margin: "0.5em 0"},
                                "& ul, & ol": {margin: "0.5em 0", paddingLeft: "1.5em"},
                                "& li": {margin: "0.75em 0"},
                                "& h1, & h2, & h3, & h4, & h5, & h6": {margin: "0.75em 0 0.5em 0"},
                                "& strong": {fontWeight: "bold"},
                                "& em": {fontStyle: "italic"},
                            }}>
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.aiResellOverview}</ReactMarkdown>
                            </Box>
                        </Box>
                    )}
                    {data.aiMakeAndModelHistory && (
                        <Box sx={{mb: 2}}>
                            <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1}}>
                                <Typography variant="subtitle2" sx={{color: theme.custom.labelColour}}>
                                    Make and Model History
                                </Typography>
                                <SpeakButton
                                    text={makeAndModelHistoryText}
                                    title="Speak make and model history"
                                    playingText={playingText}
                                    onSpeak={handleSpeak}
                                    visible={showSpeakButtons}
                                />
                            </Box>
                            <Box sx={{
                                fontSize: "0.875rem",
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                                "& p": {margin: "0.5em 0"},
                                "& ul, & ol": {margin: "0.5em 0", paddingLeft: "1.5em"},
                                "& li": {margin: "0.75em 0"},
                                "& h1, & h2, & h3, & h4, & h5, & h6": {margin: "0.75em 0 0.5em 0"},
                                "& strong": {fontWeight: "bold"},
                                "& em": {fontStyle: "italic"},
                            }}>
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.aiMakeAndModelHistory}</ReactMarkdown>
                            </Box>
                        </Box>
                    )}
                    {guestPreview && (
                        <Box sx={{mb: 2}}>
                            <Typography variant="subtitle2" sx={{color: theme.custom.labelColour, mb: 1}}>
                                Notes
                            </Typography>
                            <Typography variant="body2" sx={{mb: 1}}>
                                ...
                            </Typography>
                            <GuestLoginPrompt />
                        </Box>
                    )}
                    {!guestPreview && (
                        <>
                            {!targetMarketAfterPricing && targetMarketSection}
                            {data.aiResellNotes && (
                        <Box sx={{mb: 2}}>
                            <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1}}>
                                <Typography variant="subtitle2" sx={{color: theme.custom.labelColour}}>
                                    Notes
                                </Typography>
                                <SpeakButton
                                    text={notesText}
                                    title="Speak notes"
                                    playingText={playingText}
                                    onSpeak={handleSpeak}
                                    visible={showSpeakButtons}
                                />
                            </Box>
                            <Box sx={{
                                fontSize: "0.875rem",
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                                "& p": {margin: "0.5em 0"},
                                "& ul, & ol": {margin: "0.5em 0", paddingLeft: "1.5em"},
                                "& li": {margin: "0.75em 0"},
                                "& h1, & h2, & h3, & h4, & h5, & h6": {margin: "0.75em 0 0.5em 0"},
                                "& strong": {fontWeight: "bold"},
                                "& em": {fontStyle: "italic"},
                            }}>
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.aiResellNotes}</ReactMarkdown>
                            </Box>
                        </Box>
                    )}
                    {data.aiWorkAndRepairs && (
                        <Box sx={{mb: 2}}>
                            <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1}}>
                                <Typography variant="subtitle2" sx={{color: theme.custom.labelColour}}>
                                    Work and Repairs
                                </Typography>
                                <SpeakButton
                                    text={workAndRepairsText}
                                    title="Speak work and repairs"
                                    playingText={playingText}
                                    onSpeak={handleSpeak}
                                    visible={showSpeakButtons}
                                />
                            </Box>
                            <Box sx={{
                                fontSize: "0.875rem",
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                                "& p": {margin: "0.5em 0"},
                                "& ul, & ol": {margin: "0.5em 0", paddingLeft: "1.5em"},
                                "& h1, & h2, & h3, & h4, & h5, & h6": {margin: "0.75em 0 0.5em 0"},
                                "& strong": {fontWeight: "bold"},
                                "& em": {fontStyle: "italic"},
                                "& table": {
                                    borderCollapse: "collapse",
                                    width: "100%",
                                    margin: "0.5em 0",
                                    display: "block",
                                    overflowX: "auto",
                                },
                                "& th, & td": {
                                    border: `1px solid ${theme.palette.divider}`,
                                    padding: {xs: "6px 4px", sm: "6px 10px"},
                                    textAlign: "left",
                                    wordBreak: "break-word",
                                },
                                "& th": {
                                    backgroundColor: theme.palette.action.hover,
                                    fontWeight: "bold",
                                },
                                "& tr:nth-of-type(even)": {
                                    backgroundColor: theme.palette.action.hover,
                                },
                                "& td:first-of-type": {
                                    minWidth: {xs: "90px"},
                                },
                            }}>
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    components={{
                                        th: ({children, ...props}) => {
                                            const text = String(children).toLowerCase().trim();
                                            if (isMobile && text === "item") {
                                                return (
                                                    <th {...props} style={{minWidth: "90px"}}>
                                                        {children}
                                                    </th>
                                                );
                                            }
                                            if (isMobile && text.includes("estimated cost")) {
                                                return (
                                                    <th {...props} style={{minWidth: "60px", width: "60px"}}>
                                                        Est.<br />Cost
                                                    </th>
                                                );
                                            }
                                            return <th {...props}>{children}</th>;
                                        },
                                    }}
                                >
                                    {workAndRepairsMarkdown}
                                </ReactMarkdown>
                            </Box>
                        </Box>
                    )}
                    {data.aiCampervanConversion && (
                        <Box sx={{mb: 2}}>
                            <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1}}>
                                <Typography variant="subtitle2" sx={{color: theme.custom.labelColour}}>
                                    Campervan Conversion
                                </Typography>
                                <SpeakButton
                                    text={campervanText}
                                    title="Speak campervan conversion"
                                    playingText={playingText}
                                    onSpeak={handleSpeak}
                                    visible={showSpeakButtons}
                                />
                            </Box>
                            <Box sx={{
                                fontSize: "0.875rem",
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                                "& p": {margin: "0.5em 0"},
                                "& ul, & ol": {margin: "0.5em 0", paddingLeft: "1.5em"},
                                "& li": {margin: "0.75em 0"},
                                "& h1, & h2, & h3, & h4, & h5, & h6": {margin: "0.75em 0 0.5em 0"},
                                "& strong": {fontWeight: "bold"},
                                "& em": {fontStyle: "italic"},
                            }}>
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.aiCampervanConversion}</ReactMarkdown>
                            </Box>
                        </Box>
                    )}
                    {data.aiValueAddImprovements && (
                        <Box sx={{mb: 2}}>
                            <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1}}>
                                <Typography variant="subtitle2" sx={{color: theme.custom.labelColour}}>
                                    Value Add Improvements
                                </Typography>
                                <SpeakButton
                                    text={valueAddImprovementsText}
                                    title="Speak value add improvements"
                                    playingText={playingText}
                                    onSpeak={handleSpeak}
                                    visible={showSpeakButtons}
                                />
                            </Box>
                            <Box sx={{
                                fontSize: "0.875rem",
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                                "& p": {margin: "0.5em 0"},
                                "& ul, & ol": {margin: "0.5em 0", paddingLeft: "1.5em"},
                                "& li": {margin: "0.75em 0"},
                                "& h1, & h2, & h3, & h4, & h5, & h6": {margin: "0.75em 0 0.5em 0"},
                                "& strong": {fontWeight: "bold"},
                                "& em": {fontStyle: "italic"},
                            }}>
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.aiValueAddImprovements}</ReactMarkdown>
                            </Box>
                        </Box>
                    )}
                    {canViewPricing && (
                        <Box sx={{mb: 2}}>
                            <Typography variant="subtitle2" sx={{color: theme.custom.labelColour, mb: 1}}>
                                Pricing
                            </Typography>
                            <Table
                                size="small"
                                sx={{
                                    fontSize: "0.875rem",
                                    width: "max-content",
                                    maxWidth: "100%",
                                    tableLayout: "auto",
                                    "& .MuiTableBody-root .MuiTableRow-root": {border: "none"},
                                }}
                            >
                                <TableBody>
                                    <TableRow sx={{border: "none"}}>
                                        <TableCell sx={{color: theme.palette.text.primary, border: "none", padding: "4px 1.25rem 4px 1.5em", whiteSpace: "nowrap", fontWeight: "bold", position: "relative", width: "1%"}}>
                                            <span style={{position: "absolute", left: "0.5em"}}>•</span>
                                            Asking Price:
                                        </TableCell>
                                        <TableCell sx={{color: theme.palette.text.primary, border: "none", padding: "4px 0", textAlign: "left", whiteSpace: {xs: "normal", sm: "nowrap"}, wordBreak: "break-word"}}>
                                            {currencySymbol || "£"}{askingPrice?.toLocaleString()}
                                        </TableCell>
                                    </TableRow>
                                    {data.aiSuggestedPriceLow !== null && data.aiSuggestedPriceHigh !== null && (
                                        <TableRow sx={{border: "none"}}>
                                            <TableCell sx={{color: theme.palette.text.primary, border: "none", padding: "4px 1.25rem 4px 1.5em", whiteSpace: "nowrap", fontWeight: "bold", position: "relative", width: "1%"}}>
                                                <span style={{position: "absolute", left: "0.5em"}}>•</span>
                                                <Tooltip title={SUGGESTED_PRICE_HINT}>
                                                    <span>Suggested Price:</span>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell sx={{color: theme.palette.text.primary, border: "none", padding: "4px 0", textAlign: "left", whiteSpace: {xs: "normal", sm: "nowrap"}, wordBreak: "break-word"}}>
                                                {currencySymbol || "£"}{data.aiSuggestedPriceLow.toLocaleString()} to {currencySymbol || "£"}{data.aiSuggestedPriceHigh.toLocaleString()}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {data.aiRepairCost !== null && (
                                        <TableRow sx={{border: "none"}}>
                                            <TableCell sx={{color: theme.palette.text.primary, border: "none", padding: "4px 1.25rem 4px 1.5em", whiteSpace: "nowrap", fontWeight: "bold", position: "relative", width: "1%"}}>
                                                <span style={{position: "absolute", left: "0.5em"}}>•</span>
                                                Estimate Repair Cost:
                                            </TableCell>
                                            <TableCell sx={{color: theme.palette.text.primary, border: "none", padding: "4px 0", textAlign: "left", whiteSpace: {xs: "normal", sm: "nowrap"}, wordBreak: "break-word"}}>
                                                {currencySymbol || "£"}{data.aiRepairCost.toLocaleString()}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {data.priceRating && (
                                        <TableRow sx={{border: "none"}}>
                                            <TableCell sx={{color: theme.palette.text.primary, border: "none", padding: "4px 1.25rem 4px 1.5em", whiteSpace: "nowrap", fontWeight: "bold", position: "relative", width: "1%"}}>
                                                <span style={{position: "absolute", left: "0.5em"}}>•</span>
                                                Price Rating:
                                            </TableCell>
                                            <TableCell sx={{color: theme.palette.text.primary, border: "none", padding: "4px 0", textAlign: "left"}}>
                                                <PriceRatingTag rating={data.priceRating} />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Box>
                    )}
                    {targetMarketAfterPricing && targetMarketSection}
                        </>
                    )}
                </Box>
                </Collapse>
            </Box>
        </Box>
    );
}
